import { useCallback } from 'react';
import { ReportActionActionName } from '@amzn/handshake-painter-typescript-client/v1';
import { useSearchParams, useLocation } from 'react-router';
import { useLocalizationContext } from '@amzn/react-arb-tools';
import { useTheme } from '@amzn/meridian/theme';
import { localizationContextBuilder } from 'src/i18n/utils/mbmOptions';
import { useReportAction } from 'src/hooks/useReportAction';
import { useAnalyticsContext } from 'src/common/metrics';
import { AuthStatus, AUTH_REASON_PARAM, AUTH_SOURCE_PARAM } from 'src/utils/auth';
import { getExperimentArm } from 'src/utils/experiments';
import { getLinkedStatusAtSessionStart } from 'src/utils/getLinkedStatus';
/**
 * Hook that handles processing the different flows when app init is successful.
 */
export const useOnAppInitSuccess = () => {
    const { mutate: reportAction } = useReportAction();
    const [searchParams, setSearchParams] = useSearchParams();
    const location = useLocation();
    const rumClient = useAnalyticsContext();
    const { mode } = useTheme();
    const { setLocalizationContext } = useLocalizationContext();
    const onAppInitSuccess = useCallback((appInitData) => {
        const authReasonValue = searchParams.get(AUTH_REASON_PARAM);
        const authSourceValue = searchParams.get(AUTH_SOURCE_PARAM);
        const linkedAtSessionStart = getLinkedStatusAtSessionStart(searchParams, appInitData.linked);
        const newParams = new URLSearchParams(searchParams);
        /**
         * Since there's an edge case (transient) where SMAs clear session storage/cookies after the user goes
         * through the auth flow, we need to persist joinId and linked values in the search params of the URL.
         */
        if (!newParams.has('ias_join_id') && appInitData.joinId) {
            newParams.set('ias_join_id', appInitData.joinId);
        }
        if (!newParams.has('linked') && appInitData.linked !== undefined) {
            newParams.set('linked', appInitData.linked.toString());
        }
        // hsp (Handshake Program) param is set in App.tsx before useAppInit
        const hsp = newParams.get('hsp') || 'unknown';
        // Temporary query param to enable redirect to legacy painter. This prevents multiple bouncing boxes.
        // TODO: Remove after fully migrated
        newParams.set('redirectToPdp', 'true');
        // Report the theme the user actually rendered: the WEBLAB_PAINTER_DEPRECATE_DARK_MODE
        // T1 arm forces light regardless of OS; control (C) follows OS via `mode`. Reading `mode`
        // alone would misreport dark for T1 sessions on dark-OS devices (the gate is applied in a
        // nested Theme in App.tsx, below where this hook reads the root theme).
        const isDarkModeDeprecated = getExperimentArm(appInitData.experiments, 'WEBLAB_PAINTER_DEPRECATE_DARK_MODE') === 'T1';
        // Set session attributes based on data returned from the API
        rumClient.addSessionAttributes({
            client: appInitData.client || 'UNKNOWN',
            linked: !!appInitData.linked,
            linkedAtSessionStart,
            theme: isDarkModeDeprecated ? 'light' : mode,
            hsp,
        });
        // ACCESS_TO_PAINTER_2 is the V1-vs-V2 denominator. It also carries the WEBLAB_PAINTER_PDP_BUY_CTA
        // arm so it can serve as the per-arm denominator for the PDP_BUY_CTA_CLICK rate: the dashboard
        // divides PDP_BUY_CTA_CLICK{arm} by ACCESS_TO_PAINTER_2{arm} (same event-over-event shape the
        // 2.0-redirect metrics use). The arm is normalized to 'T1' | 'C' — identical to how the
        // ContinueButton numerator derives it (isBuyCtaEnabled === 'T1') — so a hypothetical T2/T3 arm
        // is bucketed to control on BOTH numerator and denominator and the ratio stays consistent.
        // Defaults to 'C' when the weblab is absent.
        const buyCtaArm = getExperimentArm(appInitData.experiments, 'WEBLAB_PAINTER_PDP_BUY_CTA') === 'T1' ? 'T1' : 'C';
        rumClient.recordEvent('ACCESS_TO_PAINTER_2', { arm: buyCtaArm });
        // Set locale based on data returned from the API
        if (appInitData.locale) {
            const localizationContext = localizationContextBuilder.withLocale(appInitData.locale).build();
            setLocalizationContext(localizationContext);
        }
        // Return from auth flow
        if (authReasonValue) {
            rumClient.recordEvent('RETURNED_FROM_AUTH_FLOW', {
                authReason: authReasonValue,
                authSource: authSourceValue,
            });
            // Always remove authReason and authSource from URL
            newParams.delete(AUTH_REASON_PARAM);
            newParams.delete(AUTH_SOURCE_PARAM);
            // Only send account link event for AUTHENTICATE
            if (authReasonValue === AuthStatus.AUTHENTICATE) {
                // Send account link event if user is now linked
                if (!!appInitData.linked && !linkedAtSessionStart) {
                    reportAction({
                        actionName: ReportActionActionName.AccountLinked,
                        source: authSourceValue,
                    });
                }
                else {
                    rumClient.recordError(new Error(`Auth reason exists with incorrect state: linked=${appInitData.linked}, linkedStatusAtSessionStart=${linkedAtSessionStart}`));
                }
            }
        }
        // Warning: If history.replace() is used to update the URL without explicitly merging the existing
        // location.state, the state can be inadvertently overwritten which will affect the ThankYouPage
        // retaining the purchaseId on page refreshes
        if (newParams.toString() !== searchParams.toString()) {
            setSearchParams(newParams, { replace: true, state: location.state });
        }
    }, [searchParams, rumClient, reportAction, location.state, mode, setLocalizationContext, setSearchParams]);
    return onAppInitSuccess;
};
